import {
  ConfirmationMessageTypeEnum,
  type HITLDecision,
  type HITLRequestConfirmationMessage,
  type PromptOption,
  type TerraUIMessage,
  type UserConfirmationMessage,
} from '@terra/shared-dto-chat-types';
import {
  findHITLRequestMessageIndex,
  getHITLTargetIds,
} from '@terra/ui-design-system';
import { useBackgroundExecutionStore } from '../store/backgroundExecutionStore';
import { hitlEditCallbackRegistry } from '../store/hitlEditCallbackRegistry';
import { useChatInstancesStore } from '../store/chatInstancesStore';
import { getTerminalApprovalSettings } from '../store/terminalApprovalStore';
import { getAuthHeaders } from './authHeaders';
import { getCurrentGatewayUrl } from './environmentDetection';

type LocalUserConfirmationMessage = {
  parts: TerraUIMessage['parts'];
  role: 'user';
  metadata: UserConfirmationMessage;
};

export async function resumeCopilotSubagentHitl({
  chatId,
  toolCallId,
  decision,
  decisions,
}: {
  chatId: string;
  toolCallId: string;
  decision: HITLDecision;
  decisions?: HITLDecision[];
}): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${getCurrentGatewayUrl()}/chat/graphs/co-pilot/${chatId}/resume-subagent`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        toolCallId,
        decision,
        decisions: decisions && decisions.length > 0 ? decisions : [decision],
        terminalApproval: getTerminalApprovalSettings(),
      }),
    },
  );

  if (!response.ok) {
    throw new Error(await readResumeFailureMessage(response));
  }
}

async function readResumeFailureMessage(response: Response): Promise<string> {
  const fallback = `Failed to resume background subagent (${response.status})`;
  const body: unknown = await response.json().catch((parseError: unknown) => {
    console.warn(
      'Resume failure body was not JSON; using status text',
      parseError,
    );

    return undefined;
  });

  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return fallback;
  }

  const message = (body as Record<string, unknown>).message;

  return typeof message === 'string' && message.trim() ? message : fallback;
}

export async function submitCopilotHitlDecision({
  chatId,
  dataMessage,
  selectedOption,
  decision,
  decisions,
  sendMessage,
}: {
  chatId: string;
  dataMessage: HITLRequestConfirmationMessage;
  selectedOption: PromptOption;
  decision: HITLRequestConfirmationMessage['decision'];
  decisions?: HITLDecision[];
  sendMessage: (message: {
    parts: TerraUIMessage['parts'];
    role: 'user';
    metadata: UserConfirmationMessage;
  }) => Promise<unknown>;
}): Promise<void> {
  const actionRequests =
    dataMessage.hitlRequestConfirmationPrompt?.actionRequests ?? [];
  const actionRequest = actionRequests[0];
  const toolName = actionRequest?.name || 'unknown';

  if (!decision) {
    return;
  }

  const applyDraftToDecision = (
    item: HITLDecision,
    index: number,
  ): HITLDecision => {
    if (item.type !== 'approve' || !dataMessage.id) {
      return item;
    }

    const request = actionRequests[index];
    const draftArgs =
      hitlEditCallbackRegistry.getDraftArgs(`${dataMessage.id}-${index}`) ??
      (index === 0
        ? hitlEditCallbackRegistry.getDraftArgs(dataMessage.id)
        : undefined);

    if (!draftArgs || !request) {
      return item;
    }

    return {
      ...item,
      type: 'edit',
      editedAction: { name: request.name, args: draftArgs },
    };
  };

  const resolvedDecisions = decisions?.map(applyDraftToDecision);
  const draftArgs =
    !decisions && dataMessage.id
      ? hitlEditCallbackRegistry.getDraftArgs(dataMessage.id)
      : undefined;
  const resolvedDecision: HITLRequestConfirmationMessage['decision'] =
    decision.type === 'approve' && draftArgs && actionRequest
      ? {
          ...decision,
          type: 'edit',
          editedAction: { name: actionRequest.name, args: draftArgs },
        }
      : decision;

  const rejectedDecisions = (resolvedDecisions ?? [resolvedDecision]).filter(
    (item): item is Extract<HITLDecision, { type: 'reject' }> =>
      item.type === 'reject',
  );
  const userProvidedMessage = rejectedDecisions
    .map((item) => item.message)
    .filter((message): message is string => Boolean(message))
    .join('\n\n');
  const responseValue = decisions
    ? rejectedDecisions.length > 0
      ? 'reject'
      : 'approve'
    : resolvedDecision?.type === 'edit' && decision.type === 'approve'
      ? 'edit'
      : selectedOption?.value || '';
  const confirmationSubs =
    decisions
      ?.map((item) => item.toolCallId ?? item.id)
      .filter((id): id is string => Boolean(id)) ??
    (dataMessage.id ? [dataMessage.id] : []);
  const hasVisibleText = userProvidedMessage.length > 0;
  const confirmation: UserConfirmationMessage = {
    subs: confirmationSubs,
    response: responseValue,
    message: userProvidedMessage,
    toolName,
    decision: resolvedDecision,
    decisions: resolvedDecisions,
    actionRequest,
    type: ConfirmationMessageTypeEnum.UserConfirmation,
  };
  const userMessage: LocalUserConfirmationMessage = {
    parts: hasVisibleText ? [{ type: 'text', text: userProvidedMessage }] : [],
    role: 'user',
    metadata: confirmation,
  };

  const backgroundToolCallId = dataMessage.backgroundSubagent?.toolCallId;

  if (backgroundToolCallId) {
    await resumeCopilotSubagentHitl({
      chatId,
      toolCallId: backgroundToolCallId,
      decision: resolvedDecision,
      decisions: resolvedDecisions ?? [resolvedDecision],
    });
    appendLocalUserConfirmation({ chatId, dataMessage, userMessage });
    clearSubagentWaitingInput(backgroundToolCallId);

    return;
  }

  await sendMessage(userMessage);
}

function readFirstDecidedActionId(
  confirmation: UserConfirmationMessage,
): string | undefined {
  const firstDecision = confirmation.decisions?.[0] ?? confirmation.decision;

  return (
    firstDecision?.toolCallId ?? firstDecision?.id ?? confirmation.subs?.[0]
  );
}

export function insertLocalUserConfirmation({
  messages,
  dataMessage,
  userMessage,
}: {
  messages: TerraUIMessage[];
  dataMessage: HITLRequestConfirmationMessage;
  userMessage: LocalUserConfirmationMessage;
}): TerraUIMessage[] {
  const firstActionId = readFirstDecidedActionId(userMessage.metadata);
  const confirmationId = firstActionId
    ? `user-confirmation-${firstActionId}`
    : crypto.randomUUID();

  if (messages.some((message) => message.id === confirmationId)) {
    return messages;
  }

  const hitlMessageIndex = findHITLRequestMessageIndex({
    messages,
    targetMessageIds: getHITLTargetIds({ dataMessage }),
  });
  const insertIndex =
    hitlMessageIndex >= 0 ? hitlMessageIndex + 1 : messages.length;

  return [
    ...messages.slice(0, insertIndex),
    { id: confirmationId, ...userMessage },
    ...messages.slice(insertIndex),
  ];
}

function appendLocalUserConfirmation({
  chatId,
  dataMessage,
  userMessage,
}: {
  chatId: string;
  dataMessage: HITLRequestConfirmationMessage;
  userMessage: LocalUserConfirmationMessage;
}): void {
  const { chats, configs } = useChatInstancesStore.getState();
  const chat = chats.get(chatId);
  const setMessages = configs.get(chatId)?.setMessages;

  if (!chat || !setMessages) {
    return;
  }

  const nextMessages = insertLocalUserConfirmation({
    messages: chat.messages,
    dataMessage,
    userMessage,
  });

  if (nextMessages !== chat.messages) {
    setMessages(nextMessages);
  }
}

function clearSubagentWaitingInput(toolCallId: string): void {
  const store = useBackgroundExecutionStore.getState();
  const existing = store.getPendingExecution(toolCallId);

  if (!existing || existing.kind !== 'subagent') {
    return;
  }

  store.setPendingExecution({
    ...existing,
    waitingInput: undefined,
    pausedOnParentTurn: undefined,
  });
}
